import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    DropdownDescriptiveMenu,
    DropdownMenu,
    Slider,
    Input,
    Label,
    Text,
    TooltipTrigger,
    isTextOverflowing,
} from '@spark';
import './Hyperparams.css';
import './AdvancedDropdownPanel.css';

const BETA_MIN = 0;
const BETA_MAX = 2;

/** "Optimize on" default: the metric is computed over every label. */
export const ALL_LABELS_OPTION_LABEL = 'All labels';

const metricOptions = [
    { value: 'recall', title: 'Recall', description: 'Of all the items it should have found, how many did it find?', tooltip: 'The percentage of important items that the system successfully found. Higher recall means the system is less likely to miss relevant items.' },
    { value: 'precision', title: 'Precision', description: 'When the model says ‘yes,’ how often is it right?', tooltip: 'The percentage of items flagged by the system that were actually correct. Higher precision means the system makes fewer mistakes.' },
    { value: 'accuracy', title: 'Accuracy', description: 'How often was the model correct overall?', tooltip: 'The overall percentage of correct predictions made by the system, out of all predictions.' },
    { value: 'f_beta_score', title: 'F score', description: 'Balance between precision and recall', tooltip: 'If beta is less than 1, more weight is given to precision (correctness). If beta is greater than 1, more weight is given to recall (finding as many relevant items as possible). for example, F1-Score is for β=1.' },
];

const clampBeta = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return BETA_MIN;
    const roundedToTenth = Math.round(numeric * 10) / 10;
    return Math.max(BETA_MIN, Math.min(BETA_MAX, roundedToTenth));
};

const formatBeta = (value) => String(clampBeta(value));

/**
 * Shows `text` as a tooltip over a select trigger only while the trigger's
 * displayed value is ellipsis-clipped. The Spark select renders the value in
 * `.spark-input-select-value`, which we measure after every text change and
 * on resize; a value that fits gets no tooltip.
 */
const ClippedValueTooltip = ({ text, children }) => {
    const wrapRef = useRef(null);
    const [clipped, setClipped] = useState(false);

    useLayoutEffect(() => {
        const measure = () => {
            const valueEl = wrapRef.current?.querySelector('.spark-input-select-value');
            setClipped(valueEl ? isTextOverflowing(valueEl) : false);
        };
        measure();
        if (typeof ResizeObserver === 'undefined' || !wrapRef.current) {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(wrapRef.current);
        return () => observer.disconnect();
    }, [text]);

    return (
        <span ref={wrapRef} className="hyperparams__select-wrap">
            <TooltipTrigger text={text} disabled={!clipped || !text} placement="top">
                {children}
            </TooltipTrigger>
        </span>
    );
};

/**
 * "Optimize on" — which label the optimisation metric is computed for.
 * Rendered only when the task qualifies (single-label classification with
 * exactly two labels, see `rareClassEligibleLabels`); "All labels" (null) is
 * the default and matches today's behaviour.
 */
const RareClassLabelSelect = ({ options, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const items = [
        {
            label: ALL_LABELS_OPTION_LABEL,
            state: value == null ? 'selected' : 'default',
            onClick: () => {
                onChange(null);
                setOpen(false);
            },
        },
        ...options.map((label) => ({
            label,
            state: label === value ? 'selected' : 'default',
            onClick: () => {
                onChange(label);
                setOpen(false);
            },
        })),
    ];
    // A stale value (label renamed since it was picked) falls back to the default.
    const text = value != null && options.includes(value) ? value : ALL_LABELS_OPTION_LABEL;

    return (
        <div className="hyperparams__target">
            <Label label="Optimize on" showAsterisk={false} showIcon={false} />
            <ClippedValueTooltip text={text}>
                <DropdownMenu
                    selectType="single-select"
                    showLabel={false}
                    size="medium"
                    placeholder={ALL_LABELS_OPTION_LABEL}
                    text={text}
                    panelLayout="overlay"
                    open={open}
                    onOpenChange={setOpen}
                    items={items}
                    className="hyperparams-target-dropdown"
                    panelClassName="train-advanced-dropdown-panel"
                />
            </ClippedValueTooltip>
        </div>
    );
};

const Hyperparams = (props) => {
    const selectedMetric = metricOptions.find(opt => opt.value === props.metricOptimization);
    const beta = clampBeta(props.betaForFScore ?? 1);
    const [betaInput, setBetaInput] = useState(formatBeta(beta));
    const inputFocusedRef = useRef(false);
    const rareClassLabelOptions = Array.isArray(props.rareClassLabelOptions)
        ? props.rareClassLabelOptions
        : [];

    useEffect(() => {
        // Keep the text field in sync with slider-driven changes, but do not
        // overwrite mid-edit drafts (e.g. "1." while typing 1.5).
        if (!inputFocusedRef.current) {
            setBetaInput(formatBeta(beta));
        }
    }, [beta]);

    const commitBeta = (value) => {
        const clamped = clampBeta(value);
        if (props.onFScoreBetaChange) {
            props.onFScoreBetaChange(clamped);
        }
        setBetaInput(formatBeta(clamped));
        return clamped;
    };

    const handleSliderChange = (value) => {
        commitBeta(value);
    };

    const handleInputChange = (e) => {
        const raw = e.target.value;
        // Allow empty / partial input while typing (e.g. "", "-", "1.", "0.")
        if (raw === '' || raw === '-' || raw === '.' || raw === '-.') {
            setBetaInput(raw);
            return;
        }
        if (!/^-?\d*\.?\d*$/.test(raw)) {
            return;
        }

        const numeric = Number(raw);
        if (Number.isNaN(numeric)) {
            setBetaInput(raw);
            return;
        }

        const clamped = clampBeta(numeric);
        // Keep trailing "." so users can type decimals; otherwise snap
        // out-of-range values to the [0, 2] border in the field itself.
        if (raw.endsWith('.') || (numeric >= BETA_MIN && numeric <= BETA_MAX)) {
            setBetaInput(raw);
        } else {
            setBetaInput(formatBeta(clamped));
        }

        if (props.onFScoreBetaChange) {
            props.onFScoreBetaChange(clamped);
        }
    };

    const handleInputFocus = () => {
        inputFocusedRef.current = true;
    };

    const handleInputBlur = () => {
        inputFocusedRef.current = false;
        commitBeta(
            betaInput === '' || betaInput === '-' || betaInput === '.' || betaInput === '-.'
                ? beta
                : betaInput
        );
    };

    const items = metricOptions.map((opt) => ({
        title: opt.title,
        description: opt.description,
    }));

    const selectedLabel = selectedMetric?.title || '';

    return (
        <div className="hyperparams">
            <div className="hyperparams__controls">
                <div className="hyperparams__metric">
                    <Label label="Metric Optimisation" showAsterisk={false} showIcon={false} />
                    <ClippedValueTooltip text={selectedLabel}>
                        <DropdownDescriptiveMenu
                            panelLayout="overlay"
                            placeholder="Select Metric"
                            value={selectedLabel}
                            items={items}
                            onSelect={(item, index) => {
                                props.onMetricOptimizationChange(metricOptions[index].value);
                            }}
                            size="medium"
                            className="hyperparams-metric-dropdown"
                            panelClassName="train-advanced-dropdown-panel"
                        />
                    </ClippedValueTooltip>
                </div>
                {rareClassLabelOptions.length > 0 && (
                    <RareClassLabelSelect
                        options={rareClassLabelOptions}
                        value={props.rareClassLabel ?? null}
                        onChange={(value) => props.onRareClassLabelChange?.(value)}
                    />
                )}
                {props.metricOptimization === 'f_beta_score' && (
                    <div className="hyperparams__beta">
                        <Slider
                            type="basic"
                            min={BETA_MIN}
                            max={BETA_MAX}
                            step={0.1}
                            value={beta}
                            onChange={handleSliderChange}
                            values={false}
                            showInput={false}
                            className="hyperparams__slider"
                        />
                        <div className="hyperparams-beta-wrap">
                            <span className="hyperparams-beta-label">Beta</span>
                            <Input
                                size="small"
                                label="Beta"
                                showLabel={false}
                                value={betaInput}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                                className="hyperparams-beta-input"
                                inputMode="decimal"
                            />
                        </div>
                    </div>
                )}
            </div>
            {props.metricOptimization === 'f_beta_score' && (
                <Text variant="base-medium" color="secondary" as="p" className="hyperparams__help">
                    If beta is less than 1, more weight is given to precision (correctness). If beta is
                    greater than 1, more weight is given to recall (finding as many relevant items as
                    possible). for example, F1-Score is for β=1.
                </Text>
            )}
        </div>
    );
};

export default Hyperparams;
